import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IonButton, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonPage, IonRow, IonToggle } from '@ionic/react';
import './Artistic.css';
import { add, arrowBack, closeCircleOutline, colorPalette, documentText, folderOutline, refreshOutline, remove } from 'ionicons/icons';
import { _ALIAS_SUFFIX, _defaultButtonColor, _defaultParams, _dict } from '../constants';
import { useGlobalStore } from '../stateManagement/globalZustand';
import { useUIStore } from '../stateManagement/UIZustand';
import { useTreeStore } from '../stateManagement/treeZustand';
import { ArtistiCard } from './ArtistiCard';
import { exportPDF, resetCardsInRows, scrollToBottomThenTop } from '../../foo/artistic';
import { useURLStore } from '../stateManagement/URLZustand';
import { getNonEmptyLabels } from '../../foo/tree';
import _ from 'lodash';
import { CardProps, useArtStore } from '../stateManagement/artZustand';
import SaveLayoutModal from './SaveLayoutModal';
import './Artistic.css';
import ResetChoices from './ResetChoices';
import { label2LabelToShow } from '../../foo/cards';


interface ArtistComponentProps {
  layerIndex: number | string; // Used to retrieve stored data
  setCurrentLayerIndex: any;
}


const ArtistComponent: React.FC<ArtistComponentProps> = (props) => {
  const [initialized, setInitialized] = useState<boolean>(false); // State for selected cards
  const [isEditMode, setIsEditMode] = useState<boolean>(false); // State for selected cards
  const { lang } = useGlobalStore();
  const { isArtistMode, setIsArtistMode } = useUIStore();
  const { nodes, l2Image, defaultParams, path2Params } = useTreeStore();
  const { URLVars } = useURLStore();
  const { path2art, setPathItems } = useArtStore();
  const [isStored, setIsStored] = useState<boolean>(true); // State for selected cards
  const [showStoreDialog, setShowStoreDialog] = useState<boolean>(false); // State for selected cards
  const cardRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const mainContentDivRef = useRef<HTMLDivElement>(null);
  const [doUpdate, setDoUpdate] = useState<boolean>(false); // State for selected cards
  const { cardsStates, setCardsStates, selectedCards, setSelectedCards } = useArtStore();
  const [showResetChoices, setShowResetChoices] = useState<boolean>(false); // State for selected cards
  const [resetChoice, setResetChoice] = useState<number>(2); // State for selected cards
  const capitalizeText = path2Params[URLVars.nodePath!]?.capitalizeText || defaultParams.capitalizeText;



  // Set cardsStates from available data
  useEffect(() => {
    if (initialized === true) { return; }

    let cardsList: CardProps[] = [];

    if (resetChoice === 2) {
      // If there is data stored use it, otherwise create new cards with default values
      if (URLVars.nodePath && path2art[URLVars.nodePath!] && path2art[URLVars.nodePath!][props.layerIndex]) {
        cardsList = path2art[URLVars.nodePath][props.layerIndex];
      }
    }


    populateCardRefs(cardsList);
    if (cardsList.length > 0) {
      setCardsStates(cardsList);
    } else {
      resetCardsStates();
    }



    setInitialized(true);
  }, [initialized]);


  /*useEffect(() => {
    if (isEditMode === false && selectedCards.length > 0 && initialized) {
      unselectAll();
    }
  }, [isEditMode, initialized]);*/



  const unselectAll = useCallback(() => {
    // Directly creating a new array with mapped values ensures immutability and triggers re-render
    const newCardsStates = cardsStates.map(card => ({
      ...card,
      isSelected: false
    }));
    setCardsStates(newCardsStates);
    setSelectedCards([]);
  }, [cardsStates]);

  const populateCardRefs = useCallback((anyCardsSizedList: any[]) => {
    cardRefs.current = anyCardsSizedList.map(() => React.createRef<HTMLDivElement>());
  }, [cardsStates]);

  const resetCardsStates = useCallback(() => {
    // Get valid labels from node at current path
    const labels = getNonEmptyLabels(nodes[URLVars.nodePath!]);

    // Check if width and font size are defined in cardsStates
    // and set width and font size from path2Params,
    // otherwise use default values from _defaultParams
    const width = path2Params[URLVars.nodePath!]?.cardWidth ?? _defaultParams.cardWidth;
    const fontSize = path2Params[URLVars.nodePath!]?.fontSize ?? _defaultParams.fontSize;

    populateCardRefs(labels);
    let newCardsStates: CardProps[] = [];
    // Prepare the new cards states based on labels
    newCardsStates = labels.map((label, index) => {

      return {
        ref: cardRefs.current[index],
        index: index,
        width: width,
        height: null,
        x: 0,
        y: index * 2, // Adjust Y position to avoid overlap if needed
        fontSize: fontSize,
        mainBackgroundColor: '#ffff',
        borderColor: "black",
        textColor: 'black',
        labelBackgroundColor: 'lightgray',
        borderSize: 1,
        label: label,
        image: l2Image[label] ?? null,
        isEditMode: false,
        setIsStored: setIsStored,
        selectedCards: selectedCards
      }
    });

    setCardsStates(newCardsStates);
    setInitialized(false);
  }, [resetChoice]);


  const increaseFontSize = useCallback(() => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.fontSize++) : selectedCards.forEach(index => newCardsStates[index].fontSize++);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const decreaseFontSize = useCallback(() => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.fontSize--) : selectedCards.forEach(index => Math.max(0, newCardsStates[index].fontSize--));
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const changeMainBackgroundColor = useCallback((color: string) => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.mainBackgroundColor = color) : selectedCards.forEach(index => newCardsStates[index].mainBackgroundColor = color);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const increaseBorderSize = useCallback(() => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.borderSize++) : selectedCards.forEach(index => newCardsStates[index].borderSize++);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const decreaseBorderSize = useCallback(() => {
    let newCardsStates = _.cloneDeep(cardsStates);

    if (selectedCards.length === 0) {
        newCardsStates.forEach(card => {
            card.borderSize = Math.max(0, card.borderSize - 1);
        });
    } else {
        selectedCards.forEach(index => {
            newCardsStates[index].borderSize = Math.max(0, newCardsStates[index].borderSize - 1);
        });
    }

    setCardsStates(newCardsStates);
}, [cardsStates, selectedCards]);


  const changeBorderColor = useCallback((color: string) => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.borderColor = color) : selectedCards.forEach(index => newCardsStates[index].borderColor = color);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const changeTextColor = useCallback((color: string) => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.textColor = color) : selectedCards.forEach(index => newCardsStates[index].textColor = color);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);

  const changeLabelBackgroundColor = useCallback((color: string) => {
    let newCardsStates = _.cloneDeep(cardsStates);
    selectedCards.length === 0 ? newCardsStates.forEach(card => card.labelBackgroundColor = color) : selectedCards.forEach(index => newCardsStates[index].labelBackgroundColor = color);
    setCardsStates(newCardsStates);
  }, [cardsStates, selectedCards]);


  const storeArtisticData = (layerIndex: string | number) => {
    setPathItems(URLVars.nodePath!, layerIndex, cardsStates);
  };


  const handleResetChoice = (choice: number) => {
    if (choice === 1 || choice === 2) {
      setResetChoice(choice);
      setInitialized(false);
    } else if (choice === 3) {
      setCardsStates(resetCardsInRows(cardsStates, mainContentDivRef, cardRefs));
    }
    setShowResetChoices(false);
  };


  if (!isArtistMode || !initialized) { return <></> }

  // BUTTONS 

  const backButton = (
    <IonButton color="light" style={{ margin: 0 }} onClick={() => {
      setIsArtistMode(false);
      props.setCurrentLayerIndex(null);
    }}>
      <IonIcon icon={arrowBack} style={{ fontSize: '20px' }} slot="start" />
      {_dict.back[lang]}
    </IonButton>
  );

  // Clicking on resetButton will open ResetChoices dialog

  const resetButton = (
    <IonButton color="light" style={{ margin: 0 }}
      onClick={() => setShowResetChoices(true)}>
      <IonIcon icon={refreshOutline} slot="start" />
      {_dict.reposition[lang]}
    </IonButton>
  );

  const editModeButton = (
    <IonButton color={isEditMode ? "medium" : "warning"} onClick={() => { setIsEditMode(!isEditMode); }} >
      <IonToggle checked={!isEditMode} style={{ margin: 0 }} color={isEditMode ? "dark" : "light"} >
        {_dict.move[lang]}
      </IonToggle>
    </IonButton>
  );


  const increaseFontSizeButton = (
    <IonButton disabled={!isEditMode} onClick={() => increaseFontSize()} style={{ margin: 0 }} color="light">
      <IonIcon icon={add} style={{ fontSize: '20px' }} slot="start" />
      {_dict.text[lang]}
    </IonButton>
  );

  const decreaseFontSizeButton = (
    <IonButton disabled={!isEditMode} onClick={() => decreaseFontSize()} style={{ margin: 0 }} color="light">
      <IonIcon icon={remove} style={{ fontSize: '20px' }}
        slot="start"
      />
      {_dict.text[lang]}
    </IonButton>
  );

  const colorPickerButtonMainBackground = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => document.getElementById(`color-picker-1`)!.click()}
    >
      <IonIcon icon={colorPalette} style={{ fontSize: '20px' }}
        slot="start"
        color="dark" />
      {_dict.cardBackground[lang]}
      <input
        type="color"
        id={`color-picker-1`}
        style={{ display: 'none' }} // Hide the actual input element
        onChange={(e) => {
          changeMainBackgroundColor(e.target.value);
        }}
      />
    </IonButton>
  );

  const increaseBorderSizeButton = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => {
        increaseBorderSize();
      }}
    >
      <IonIcon icon={add} style={{ fontSize: '20px' }} slot="start" />
      {_dict.plusBorderSize[lang]}
    </IonButton>
  );

  const decreaseBorderSizeButton = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => {
        decreaseBorderSize();
      }}
    >
      <IonIcon icon={remove} style={{ fontSize: '20px' }} slot="start" />
      {_dict.minusBorderSize[lang]}
    </IonButton>
  );

  const colorPickerButtonBorderColor = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => document.getElementById(`border-color-picker-2`)!.click()}
    >
      <IonIcon icon={colorPalette} style={{ fontSize: '20px' }} color="dark" slot="start" />
      {_dict.borderColor[lang]}
      <input
        type="color"
        id={`border-color-picker-2`}
        style={{ display: 'none' }}
        onChange={(e) => {
          changeBorderColor(e.target.value);
        }}
      />
    </IonButton>
  );

  const colorPickerButtonTextColor = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => document.getElementById(`text-color-picker-3`)!.click()}
    >
      <IonIcon icon={colorPalette} style={{ fontSize: '20px' }} color="dark" slot="start" />
      {_dict.textColor[lang]}
      <input
        type="color"
        id={`text-color-picker-3`}
        style={{ display: 'none' }}
        onChange={(e) => {
          changeTextColor(e.target.value);
        }}
      />
    </IonButton>
  );

  const colorPickerButtonLabelBackgroundColor = (
    <IonButton
      disabled={!isEditMode}
      color="light"
      style={{ margin: 0 }}
      onClick={() => document.getElementById(`label-background-color-picker-4`)!.click()}
    >
      <IonIcon icon={colorPalette} style={{ fontSize: '20px' }} color="dark" slot="start" />
      {_dict.textBackgroundColor[lang]}
      <input
        type="color"
        id={`label-background-color-picker-4`}
        style={{ display: 'none' }}
        onChange={(e) => {
          changeLabelBackgroundColor(e.target.value);
        }}
      />
    </IonButton>
  );

  const unselectAllButton = (
    <IonButton
      disabled={selectedCards.length === 0}
      color="light"
      style={{ margin: 0 }}
      onClick={() => unselectAll()}
    >
      <IonIcon icon={closeCircleOutline} slot='start' />
      {_dict.unselectAll[lang]}
    </IonButton>
  );

  const storeArtisticDataButton = (
    <IonButton
      disabled={isStored}
      color="light"
      style={{ margin: 0 }}
      onClick={() => { setShowStoreDialog(true) }}
    >
      <IonIcon icon={folderOutline} slot='start' />
      {_dict.storeLayout[lang]}
    </IonButton>
  );





  const exportPDFButton = (
    <IonButton
      color="danger"
      style={{ margin: 0 }}
      onClick={async () => {
        //await scrollToRight(mainDivRef);
        await scrollToBottomThenTop(ionContentRef);
        //await scrollToLeft(mainDivRef);
        //await scrollToTop(mainDivRef);


        exportPDF("mainContent");
      }}
    >
      <IonIcon icon={documentText} slot='start' />
      {_dict.exportPDF[lang]}
    </IonButton>
  );







  return (
    <IonPage>
      <IonHeader>

        <IonGrid >
          <IonRow>
            <IonCol>
              <strong>{props.layerIndex}</strong>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol >
              {backButton}
              {resetButton}
              {storeArtisticDataButton}
              {exportPDFButton}
              {unselectAllButton}
              {editModeButton}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="auto" style={{ backgroundColor: !isEditMode ? "lightgray" : "#ffc409" }}>

              {increaseFontSizeButton}
              {decreaseFontSizeButton}
              {increaseBorderSizeButton}
              {decreaseBorderSizeButton}
              {colorPickerButtonTextColor}
              {colorPickerButtonMainBackground}
              {colorPickerButtonBorderColor}
              {colorPickerButtonLabelBackgroundColor}

            </IonCol>
            <IonCol></IonCol>
          </IonRow>
        </IonGrid>

      </IonHeader>
      <IonContent ref={ionContentRef}>


        <SaveLayoutModal
          isOpen={showStoreDialog}
          onConfirm={(layoutName: string | number) => {
            storeArtisticData(layoutName);
            props.setCurrentLayerIndex(layoutName);
            setIsStored(true);
          }}
          initialLayoutName={props.layerIndex}
          onDismiss={() => setShowStoreDialog(false)} />

        <div id="mainContent" ref={mainContentDivRef}>

          {cardsStates.map((card, index) => {

            return (
              <ArtistiCard
                ref={cardRefs.current[index]}
                key={`${doUpdate}-${index}`}
                label={label2LabelToShow(card.label, lang, capitalizeText)}
                image={card.image}
                index={index}
                fontSize={card.fontSize}
                mainBackgroundColor={card.mainBackgroundColor}
                borderColor={card.borderColor}
                textColor={card.textColor}
                labelBackgroundColor={card.labelBackgroundColor}
                borderSize={card.borderSize}
                width={card.width}
                height={card.height}
                x={card.x}
                y={card.y}
                isEditMode={isEditMode}
                setIsStored={setIsStored}
              />
            );
          })}

        </div>
        <div className="horizontalLine-1 forbiddenZone"></div>
        <div className="horizontalLine-2 forbiddenZone"></div>
        <div className="horizontalLine-3 forbiddenZone"></div>
        <div className="verticalRegion forbiddenZone"></div>
        {showResetChoices && <ResetChoices onChoiceSelected={handleResetChoice} setShowResetChoices={setShowResetChoices} />}
      </IonContent>
    </IonPage>
  );

};

export default ArtistComponent;
